<template>
	<div ref="sticky" class="tab-bar" :class="{ stuck: stickied }">
		<div class="content">
			<div v-if="stickied" class="poster" :style="{ backgroundImage: `url(${cdnHost}${game.gameCardImage})` }"></div>
			<div class="tab-list">
				<div
					v-for="tab in tabList"
					:key="tab.id"
					class="tab"
					:class="{ selected: selectedTab === tab.id }"
					@click="scrollToTab(tab.id)"
				>
					<div class="name">{{ $t(`main.details-tabs.${tab.id}`) }}</div>
					<div v-if="selectedTab === tab.id" class="selected-border"></div>
				</div>
			</div>
			<div v-if="stickied" class="download">
				<a
					v-if="game.enableCustomDownloadButton"
					class="custom"
					:href="getLocaleString(game.customDownloadButtonLink)"
					target="_blank"
					rel="external"
				>
					<span>{{ getLocaleString(game.customDownloadButtonText) }}</span>
				</a>
				<button v-if="!game.enableCustomDownloadButton && isWindows && game.pc" @click="download(game.productID)">
					<svgIcon icon-holder-class="download-icon" icon-id="icon-ext-windows" />
					<span>{{ $t('main.download') }}</span>
				</button>
				<a
					v-if="!game.enableCustomDownloadButton && isWindows && game.steam && !game.pc"
					class="steam"
					:href="game.steam"
					target="_blank"
					rel="external"
				>
					<svgIcon icon-holder-class="download-icon" icon-id="icon-steam" />
					<span>{{ $t('main.play-on-steam') }}</span>
				</a>
				<a v-if="!game.enableCustomDownloadButton && isIOS && game.ios" :href="game.ios" target="_blank" rel="external" class="ios">
					<img :src="`${staticHost}images/thirdparty-icons/itunes/itunes-${language}.svg`" />
				</a>
				<a
					v-if="!game.enableCustomDownloadButton && isAndroid && game.android"
					:href="game.android"
					target="_blank"
					rel="external"
					class="android"
				>
					<img :src="`${staticHost}images/thirdparty-icons/google-play/google-play-${language}.png`" />
				</a>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { getLocaleString } from '../../../plugins/helpers';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import downloadGame from '../../../plugins/launcherDownload';

export default {
	name: 'GameDetailsTabBar',
	components: {
		svgIcon,
	},
	props: {
		game: {
			type: Object,
			default: () => {},
		},
		tabList: {
			type: Array,
			default: () => [],
		},
		stickied: {
			type: Boolean,
			default: false,
		},
		mediaProcessed: {
			type: Boolean,
			default: false,
		},
		reqsProcessed: {
			type: Boolean,
			default: false,
		},
		refs: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			selectedTab: '',
			stickyOffset: 0,
			resizing: false,
			windowWidth: 0,
		};
	},
	computed: {
		...mapState(['isIOS', 'isAndroid', 'isWindows']),
		NXLogSessionId() {
			return this.$cookie.get('NxLogSession') ? this.$cookie.get('NxLogSession') : new Date().toISOString();
		},
		language() {
			return this.$i18n.locale;
		},
		region() {
			return environment.region;
		},
		memberShipNo() {
			return this.region === 'global' ? 1 : this.region === 'th' ? 3 : this.region === 'tw' ? 4 : this.region === 'sea' ? 5 : null;
		},
		nativeScroll() {
			return 'scrollBehavior' in document.documentElement.style;
		},
	},
	watch: {
		mediaProcessed(processed) {
			if (!processed || !this.reqsProcessed) return;
			this.setTab(this.tabList[0].id);
		},
		reqsProcessed(processed) {
			if (!processed || !this.mediaProcessed) return;
			this.setTab(this.tabList[0].id);
		},
	},
	mounted() {
		this.$nextTick(() => {
			window.addEventListener('scroll', this.handleScroll);
			window.addEventListener('resize', this.handleResize);

			this.handleResize();
		});
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
		window.removeEventListener('resize', this.handleResize);
	},
	methods: {
		setTab(tab) {
			this.selectedTab = tab;
		},
		scrollToTab(tab) {
			dataLayer.push({
				event: 'Game Detail Content Menu Click',
				gameDetailContent: this.$t(`main.details-tabs.${tab}`, 'en-US'),
				gameDetailName: this.game.name,
				gameDetailID: this.game.productID,
			});
			const top = this.refs[tab].offsetTop - this.$refs.sticky.clientHeight - 20 * (tab === 'media' ? -1 : 1) - 56;
			if (this.nativeScroll) {
				window.scrollTo({
					top,
					behavior: 'smooth',
				});
				return;
			}
			window.scrollTo(0, top);
		},
		handleResize() {
			const currentWidth = document.documentElement.clientWidth;
			if (this.windowWidth === currentWidth) return;
			this.windowWidth = currentWidth;
			this.resizing = true;
			this.$emit('setSticky', false);
			window.scrollTo(0, 0);
			this.setTab(this.tabList[0].id);
			setTimeout(() => {
				this.stickyOffset = this.$refs.sticky.offsetTop - 56;
				this.resizing = false;
				this.handleScroll();
			}, 100);
		},
		handleScroll() {
			if (this.resizing) return;
			const pageYOffset = window.pageYOffset;
			this.$emit('setSticky', pageYOffset >= this.stickyOffset);
			const tabBarHeight = this.$refs.sticky.clientHeight;
			for (let i = this.tabList.length - 1; i >= 0; i--) {
				const tab = this.tabList[i].id;
				if (i === 0 || pageYOffset >= this.refs[tab].offsetTop - tabBarHeight - 30 - 56) {
					this.setTab(tab);
					return;
				}
			}
		},
		download(prodId) {
			downloadGame(prodId, this.NXLogSessionId, this.memberShipNo, this.language, this.$route.query.partnerKey);
		},
		getLocaleString(data) {
			return getLocaleString(data, this.language);
		},
	},
};
</script>

<style lang="less" scoped>
.tab-bar {
	display: none;
	user-select: none;
}

@media (min-width: 1024px) {
	.tab-bar {
		background-color: #1f2023;
		border-bottom: 1px solid rgba(0, 0, 0, 0.5);
		display: block;
		height: 54px;
		width: 100%;
		&.stuck {
			margin: 0;
			position: fixed;
			top: 56px;
			z-index: 1;
			.content {
				.tab-list {
					.tab {
						.name {
							margin: 0 25px;
						}
					}
				}
			}
		}
		.content {
			display: flex;
			height: 100%;
			margin: auto;
			position: relative;
			width: 770px;
			.poster {
				background-position: center;
				background-size: contain;
				margin-right: 25px;
				width: 40px;
			}
			.tab-list {
				display: flex;
				flex: 1;
				.tab {
					color: #7e7e7f;
					cursor: pointer;
					font-size: 12px;
					letter-spacing: -0.24px;
					position: relative;
					text-align: center;
					transition: all 0.2s;
					&:not(:active):hover {
						color: white;
					}
					&:active {
						color: rgba(255, 255, 255, 0.5);
					}
					&.selected {
						background-color: #2a2b30;
						color: white;
					}
					.selected-border {
						background-color: #0077ff;
						bottom: 0;
						height: 3px;
						position: absolute;
						width: 100%;
					}
					.name {
						align-items: center;
						display: flex;
						height: 100%;
						margin: 0 30px;
					}
				}
			}
			.download {
				align-items: center;
				align-self: flex-end;
				display: flex;
				height: 100%;
				justify-content: center;
				button,
				a.steam,
				a.custom {
					align-items: center;
					background-color: #0077ff;
					border: none;
					border-radius: 5px;
					color: white;
					cursor: pointer;
					display: flex;
					font-size: 9.5px;
					font-weight: bold;
					height: 34px;
					justify-content: center;
					outline: 0;
					padding: 0 5px;
					text-transform: uppercase;
					transition: background-color 0.2s, background-color 0.2s, border-color 0.2s, color 0.2s;
					width: 130px;
					&:not(:active):hover {
						background-color: #4ca1ff;
					}
					&:active {
						background-color: #0052b3;
						color: #b3b3b4;
						.download-icon {
							opacity: 0.5;
						}
					}
					.download-icon {
						height: 12px;
						margin-right: 7px;
						width: 10px;
					}
				}
				a.ios {
					img {
						height: 34px;
						width: 102px;
					}
				}
				a.android {
					img {
						height: 34px;
						width: 115px;
					}
				}
			}
		}
	}
}

@media (min-width: 1280px) {
	.tab-bar {
		&.stuck {
			.content {
				.tab-list {
					.tab {
						.name {
							margin: 0 30px;
						}
					}
				}
			}
		}
		.content {
			width: 960px;
			.poster {
				margin-right: 35px;
			}
		}
	}
}

@media (min-width: 1440px) {
	.tab-bar {
		height: 70px;
		.content {
			width: 1080px;
			.poster {
				width: 52px;
			}
			.tab-list {
				.tab {
					font-size: 14px;
					letter-spacing: -0.28px;
				}
			}
			.download {
				button,
				a.steam,
				a.custom {
					font-size: 12px;
					height: 50px;
					width: 190px;
					.download-icon {
						height: 14px;
						margin-right: 9px;
						width: 12px;
					}
				}
				a.ios {
					img {
						height: 50px;
						width: 150px;
					}
				}
				a.android {
					img {
						height: 50px;
						width: 163px;
					}
				}
			}
		}
	}
}

@media (min-width: 1600px) {
	.tab-bar {
		.content {
			width: 1200px;
		}
	}
}

@media (min-width: 1920px) {
	.tab-bar {
		.content {
			width: 1440px;
		}
	}
}
</style>
